import {
  observable,
  action,
} from 'mobx';
import logger from '../utils/logger';
import { showConfirmModal } from '../modules/modals/confirmService';
import moment from 'moment';
import {
  asyncForEach,
  waitFor,
} from '../utils/async';
import { isFeatureEnabled } from '../utils/feature-flags';
import { initializeEmitter } from './batch/initializeEmitter';
import { createTimerRegistry } from '../utils/timer-manager/timer-manager';
import { remoteControlMixin } from './batch/remote-control-mixin';
import { batchCompletionMixin } from './batch/batch-completion-mixin';
import { notificationMixin } from './batch/notification-mixin';
import { timerMixin } from './batch/timer-mixin';
import { deviceStatusMixin } from './batch/device-status-mixin';
import { loadManagementMixin } from './batch/load-management-mixin';
import { pumpHelpersMixin } from './batch/pump-helpers-mixin';
import { pacCommunicationMixin } from './batch/pac-communication-mixin';
import { dischargeMixin } from './batch/discharge-operations/discharge-mixin';
import { batchEmitMixin } from './batch/batch-emit-mixin';
import { batchLifecycleMixin } from './batch/batch-lifecycle-mixin';
import { scaleWeightMixin } from './batch/scale-weight-mixin';
import { batchMaterialMixin } from './batch/batch-material-mixin';
import { manualFeedMixin } from './batch/manual-feed-mixin';
import { feedModeMixin } from './batch/feed-mode-mixin';
import { jogModeMixin } from './batch/jog-mode-mixin';
import { jogHelpersMixin } from './batch/jog-helpers-mixin';
import { pumpMeteredMixin } from './batch/pump-metered-mixin';
import { pumpDischargeMixin } from './batch/pump-discharge-mixin';
import { batchActionMixin } from './batch/batch-action-mixin';
import { startBatchMixin } from './batch/start-batch-mixin';
import { pacScaleMixin } from './batch/pac-scale-mixin';
import { dischargeSequenceMixin } from './batch/discharge-sequence-mixin';
import { dischargePrimaryMixin } from './batch/discharge-primary-mixin';
import { dischargeHelpersMixin } from './batch/discharge-helpers-mixin';
import { controlMixin } from './batch/control-mixin';
import { initListenersMixin } from './batch/init-listeners-mixin';
import { batchInitMixin } from './batch/batch-init-mixin';
import { batchHelpersMixin } from './batch/batch-helpers-mixin';
import { binScaleDataMixin } from './batch/bin-scale-data-mixin';
import type { IBatchStore } from './batch/batch-store-types';
import RootStore from './root-store';

const FEATURE_EXTENDED_ANALOG_INPUTS = isFeatureEnabled('extended_analog_inputs');

class BatchStore {
  @observable showModal = false;
  @observable inputs = new Array(48).fill('0');
  @observable outputs = new Array(96).fill('0');
  @observable channels = new Array(16).fill('0');
  @observable weightsStatus = new Array(16).fill('0');
  @observable meters = new Array(48).fill('0');
  @observable metersStatus = new Array(48).fill('0');
  @observable analogInputs = FEATURE_EXTENDED_ANALOG_INPUTS ? new Array(8).fill('0') : Array(4).fill('0');
  @observable analogOutputs = new Array(4).fill('0');
  @observable isRunning = false;
  @observable ticks = null;
  @observable orderId = null;
  @observable queue = [];
  @observable running: any[] = [];
  @observable scaleQueue = {};
  @observable isDemo = false;
  @observable isTestMode = false;
  @observable isOverride = false;
  @observable isAbort = false;
  @observable isAbortDialogVisible = false;
  @observable isDischarge = false;
  @observable discharging = false;
  @observable isHoldDischarge = false;
  @observable isHoldFeed = false;
  @observable isEmergencyStop = false;
  @observable stoppedDevices = [];
  @observable trackedFeedPumps = [];
  @observable trackedDischargeScales = [];
  @observable deviceStatus = new Map();
  _deviceStatus = new Map();
  waitingForDischarge = [];
  pacVersion = null;
  pacVersionEmitter = null;
  proxyVersion = null;
  proxyVersionEmitter = null;
  @observable proxyIsOnline = false;
  @observable proxyRequireUpdate = false;
  @observable pacConnected = false;
  @observable stopObserve = false;
  @observable isPacInitialCommandSent = false;
  @observable emitter = initializeEmitter();
  @observable canEmit = true;
  @observable observeStartTime = null;
  @observable emitTime = moment();
  @observable jogTestRunning = false;
  @observable code = null;
  @observable statusCount = 0;
  @observable statusTime = null;
  @observable indicatorError = false;
  @observable indicatorValueError = false;
  @observable holdFeedDevices = [];
  @observable finishFeedDevices = [];
  @observable pressedBinButtons = new Set();
  @observable finishDischargeDevices: number[] = [];
  @observable deferredFeedDevices = new Map();
  @observable pumpWeights = {};
  @observable scaleWeights = {};
  @observable loadHistory = [];
  @observable replayMode = false;
  @observable lastLoad = null;
  @observable logs = [];
  @observable abortWithQueue = true;
  @observable maintenanceMode = false;
  @observable batchOffline = false;
  @observable batchInfoComingTime = moment();
  @observable remoteBatchData = null;
  @observable remoteControl = false;
  @observable remoteUser = null;
  @observable newVersionAvailable = false;
  @observable materialSubstitutionData = {
    originalMaterialId: null,
    scaleId: null,
    currentWeight: null,
  };
  @observable feedMetrics = {};
  @observable mixerDownButtonShouldBeDisabled = false;
  @observable isFinishBatchPanelVisible = false;
  @observable lastLoadStartTime = null;
  @observable emitDataCounter = 0;
  @observable contaminationData = null;
  @observable overToleranceData = null;
  @observable isOverTolerancePopupOn = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.timers = createTimerRegistry('BatchStore');
    this.initListeners();
    this.checkIndicatorError();
    this.checkBeltsInterlock();
    this.checkScalesDischargeFinish();
    this.monitorPumpInventory();
    this.monitorScaleInventory();
    this.checkManualPanel();
  }

  get pac() {
    return this.isDemo || !this.canEmit || this.replayMode
      ? this.rootStore.pacSimulatorStore
      : this.rootStore.pacStore;
  }

  get pacSimulator() {
    return this.rootStore.pacSimulatorStore;
  }

  get newAuthStore() {
    return this.rootStore.newAuthStore;
  }

  get plantId() {
    return this.rootStore.locationsStore.plantId;
  }

  get materialStore() {
    return this.rootStore.materialStore;
  }

  get settingsStore() {
    return this.rootStore.settingsStore;
  }

  get devicesStore() {
    return this.rootStore.devicesStore;
  }

  get dischargeStore() {
    return this.rootStore.dischargeStore;
  }

  get ticketStore() {
    return this.rootStore.ticketStore;
  }

  get scheduleStore() {
    return this.rootStore.scheduleStore;
  }

  get maximumDropSize() {
    return (this.settingsStore.configuration?.maximumDropSize) || 0;
  }

  get smallLoadSize() {
    return (this.settingsStore.configuration?.smallLoadSize) || 0;
  }

  get maxQueueSize() {
    return this.settingsStore.configuration?.maxQueueSize || 5;
  }

  get timezone() {
    return this.settingsStore.configuration?.ianaTimeZone;
  }

  get currentTenantSlug() {
    return this.rootStore.newAuthStore.companyConfig?.slug;
  }

  get canDemoBatch() {
    const { configuration } = this.settingsStore;
    return !!configuration?.enableDemo;
  }

  get canReduceInventory() {
    const { configuration } = this.settingsStore;
    return this.canEmit
      && !this.replayMode
      && !this.isTestMode
      && (!this.maintenanceMode || (this.maintenanceMode && !!configuration?.reduceMaintenanceModeInventory))
      && (!this.isDemo || (this.isDemo && !!configuration?.reduceDemoInventory));
  }

  get isDevMode() {
    // @ts-ignore
    return !!window?.devMode;
  }

  /**
   * Queue size without drops
   */
  get queueSize(): number {
    let queue: string[] = [];
    this.queue.forEach((v: any) => {
      if (!queue.includes(v?.uid)) {
        queue.push(v.uid);
      }
    });

    return queue.length;
  }

  /**
   * Execute code from editor
   * User can inject code using editor - we have to replace the names of the functions
   *
   * @param code
   * @returns
   */
  executeCode = (code: string) => {
    if (code) {
      code = code.split('function onDeviceChange').join('this.onDeviceChange = function');
      code = code.split('function onDeviceStatusChange').join('this.onDeviceStatusChange = function');
      code = code.split('function onIOChange').join('this.onIOChange = function');
      code = code.split('function onBatchCycle').join('this.onBatchCycle = function');
      code = code.split('function onTicketArrival').join('this.onTicketArrival = function');
      code = code.split('function canStartLoad').join('this.canStartLoad = function');
      code = code.split('run(\'').join('this.pac.runWS(\'');
      code = code.split('runWithDelay([\'').join('this.runWithDelay([\'');
      code = code.split('window.alert(').join('this.showInfo(');
      code = code.split('keyPress(\'').join('this.keyPress(\'');
      code = code.split('holdScale(\'').join('this.holdScaleId(\'');
      code = code.split('resumeScale(\'').join('this.resumeScaleId(\'');
      this.context.pac = this.isTestMode ? this.pacSimulator : this.pac;
      logger.log('batch logic code:', code);
    }

    try {
      // eslint-disable-next-line
      return (new Function(`return (function(){${code}}).call(this)`)).call(this.context);
    }
    catch (e) {
      this.showError({key: 'Error, please check the editor code', props: {error: e}});
    }
  };

  /**
   * Inject code callback
   */
  injectCode = (callback: any) => {
    callback(this.code);
  };

  /**
   * Context for editor
   *
   * @type {{batchingData: Array, pac: {}, devices: Map<any, any>, onDeviceChange: BatchStore.context.onDeviceChange,
   *   queue: Array}}
   */
  context = {
    queue: this.queue,
    batchingData: this.running,
    devices: this.deviceStatus,
    pac: {}, // pac: this.pac,
    onDeviceChange: (id: number, status: string, type: string, weight: number, target: number) => {
      if (!this.canEmit) {
        return;
      }
      logger.debug('onDeviceChange:', id, status, type, weight, target);
    },
    onDeviceStatusChange: (id: number, status: string, type: string) => {
      if (!this.canEmit) {
        return;
      }
      logger.debug('onDeviceStatusChange:', id, status, type);
    },
    onIOChange: (inputs: string[], outputs: string[], channels: string[], loads: number) => {
      if (!this.canEmit) {
        return;
      }
      logger.debug('onIOChange:', inputs, outputs, channels, loads);
    },
    onBatchCycle: (status: string, inputs: string[], outputs: string[], channels: string[]) => {
      if (!this.canEmit) {
        return;
      }
      logger.debug('onBatchCycle:', status, inputs, outputs, channels);
    },
    onTicketArrival: (ticket: any, origin: string) => {
      logger.log('onTicketArrival:', ticket, origin);
    },
    showInfo: (message: string) => {
      this.showInfo(message);
    },
    holdScaleId: (scaleId: number) => {
      this.holdScaleId(scaleId);
    },
    resumeScaleId: (scaleId: number) => {
      this.resumeScaleId(scaleId);
    },
    runWithDelay: (commands = [], delay = 100) => {
      const arr = (commands?.filter(v => v)) || [];
      logger.debug('runWithDelay', arr, delay);

      const start = async () => {
        await asyncForEach(arr, async (command: string, i: number) => {
          this.pac.runWS(command);
          logger.log('runWithDelay command:', i, command);
          await waitFor(delay);
        });
      };
      start();
    },
    keyPress: (key: string) => {
      if (!this.canEmit) {
        return;
      }

      logger.log('logic editor: key press:', key);

      switch (key) {
        case 'F2':
          this.override();
          break;
        case 'F7': {
          const { sidebarStore } = this.rootStore;
          sidebarStore.onToggle('batchForm');
          break;
        }
        case 'F10':
          this.discharge(true);
          break;
        default:
          logger.warn('logic editor: wrong key press', key);
      }
    },
    canStartLoad: (inputs: string[], outputs: string[], channels: string[]) => {
      logger.log('logic editor: canStartLoad:', inputs, outputs, channels);
      return true;
    },
  };

  /**
   * Before logout, we need to clear all outputs
   */
  @action
    onBeforeLogout = () => {
      showConfirmModal({
        title: 'Are you sure you want to logout?',
        okLabel: 'Yes',
        cancelLabel: 'No',
      }).then(() => {
        if (this.canEmit && this.pacConnected) {
          this.resetAnalogOutputs().then(() => this.closeAllOutputs());
          this.finishAllLoads().then(() => {
            this.setInitialData();
          });
        }

        if (this.remoteControl) {
          this.toggleRemoteControl();
        }

        this.clearAllTimers();

        if (this.isDemo) {
          this.pacSimulator.init();
          this.pacSimulator.reset();
        }

        this.newAuthStore.logout();
      }).catch(() => {});
    };

}

// Functional mixin composition
// Each extracted mixin is applied here. The public API of BatchStore does
// not change — all method names remain the same on every instance.
// Explicit per-method assignments are used so IDEs can trace Go-to-Definition
// directly to each mixin implementation.

// remote-control-mixin
BatchStore.prototype.sendRemoteInstruction = remoteControlMixin.sendRemoteInstruction;
BatchStore.prototype.canSendRemoteInstruction = remoteControlMixin.canSendRemoteInstruction;
BatchStore.prototype.canExecuteRemoteCode = remoteControlMixin.canExecuteRemoteCode;
BatchStore.prototype.canToggleRemoteControl = remoteControlMixin.canToggleRemoteControl;
BatchStore.prototype.toggleRemoteControl = remoteControlMixin.toggleRemoteControl;
BatchStore.prototype.takeBatchControl = remoteControlMixin.takeBatchControl;

// batch-completion-mixin
BatchStore.prototype.nextStep = batchCompletionMixin.nextStep;
BatchStore.prototype.printTicketOnBatchEnd = batchCompletionMixin.printTicketOnBatchEnd;
BatchStore.prototype.saveResultForDrop = batchCompletionMixin.saveResultForDrop;
BatchStore.prototype.saveDropTimes = batchCompletionMixin.saveDropTimes;
BatchStore.prototype.sendBatchFinish = batchCompletionMixin.sendBatchFinish;
BatchStore.prototype.sendFeedLogs = batchCompletionMixin.sendFeedLogs;
BatchStore.prototype.resetSwitchLatchOn = batchCompletionMixin.resetSwitchLatchOn;
BatchStore.prototype.resetMixers = batchCompletionMixin.resetMixers;
BatchStore.prototype.finishBatch = batchCompletionMixin.finishBatch;

// notification-mixin
BatchStore.prototype.showSnackbar = notificationMixin.showSnackbar;
BatchStore.prototype.showSuccess = notificationMixin.showSuccess;
BatchStore.prototype.showInfo = notificationMixin.showInfo;
BatchStore.prototype.showWarning = notificationMixin.showWarning;
BatchStore.prototype.showError = notificationMixin.showError;

// timer-mixin
BatchStore.prototype.clearBeltsTimers = timerMixin.clearBeltsTimers;
BatchStore.prototype.clearAllTimers = timerMixin.clearAllTimers;
BatchStore.prototype.clearTimers = timerMixin.clearTimers;
BatchStore.prototype.clearExtraTimers = timerMixin.clearExtraTimers;

// device-status-mixin
BatchStore.prototype.getDeviceStatusDefault = deviceStatusMixin.getDeviceStatusDefault;
BatchStore.prototype.getDeviceStatus = deviceStatusMixin.getDeviceStatus;
BatchStore.prototype.clearDeviceStatus = deviceStatusMixin.clearDeviceStatus;
BatchStore.prototype.clearDeviceError = deviceStatusMixin.clearDeviceError;

// load-management-mixin
BatchStore.prototype.resumeLoad = loadManagementMixin.resumeLoad;
BatchStore.prototype.holdLoad = loadManagementMixin.holdLoad;
BatchStore.prototype.executeRemoveLoad = loadManagementMixin.executeRemoveLoad;
BatchStore.prototype.removeFromQueue = loadManagementMixin.removeFromQueue;
BatchStore.prototype.setInitialData = loadManagementMixin.setInitialData;
BatchStore.prototype.finishAllLoads = loadManagementMixin.finishAllLoads;
BatchStore.prototype.executeAbort = loadManagementMixin.executeAbort;

// pump-helpers-mixin
BatchStore.prototype.closePump = pumpHelpersMixin.closePump;
BatchStore.prototype.holdPump = pumpHelpersMixin.holdPump;
BatchStore.prototype.resumeFeedPump = pumpHelpersMixin.resumeFeedPump;

// pac-communication-mixin
BatchStore.prototype.setOutput = pacCommunicationMixin.setOutput;
BatchStore.prototype.setOutputs = pacCommunicationMixin.setOutputs;
BatchStore.prototype.sendLinkedOutput = pacCommunicationMixin.sendLinkedOutput;
BatchStore.prototype.setWeight = pacCommunicationMixin.setWeight;
BatchStore.prototype.resetWeight = pacCommunicationMixin.resetWeight;
BatchStore.prototype.resetIndicator = pacCommunicationMixin.resetIndicator;
BatchStore.prototype.setMeter = pacCommunicationMixin.setMeter;
BatchStore.prototype.setMeterInputs = pacCommunicationMixin.setMeterInputs;
BatchStore.prototype.resetMeter = pacCommunicationMixin.resetMeter;
BatchStore.prototype.resetCounter = pacCommunicationMixin.resetCounter;
BatchStore.prototype.setAnalog = pacCommunicationMixin.setAnalog;
BatchStore.prototype.closeOpenedOutputs = pacCommunicationMixin.closeOpenedOutputs;
BatchStore.prototype.closeAllOutputs = pacCommunicationMixin.closeAllOutputs;
BatchStore.prototype.sendPacInitialCommands = pacCommunicationMixin.sendPacInitialCommands;
BatchStore.prototype.initialCommands = pacCommunicationMixin.initialCommands;
BatchStore.prototype.resetCounters = pacCommunicationMixin.resetCounters;
BatchStore.prototype.resetAnalogOutputs = pacCommunicationMixin.resetAnalogOutputs;

// discharge-mixin
BatchStore.prototype.dischargeScale = dischargeMixin.dischargeScale;
BatchStore.prototype.areAllAdmixturesFinishDischarge = dischargeMixin.areAllAdmixturesFinishDischarge;
BatchStore.prototype.checkDischargeFinish = dischargeMixin.checkDischargeFinish;
BatchStore.prototype.isScaleFinishedDischargeEarlier = dischargeMixin.isScaleFinishedDischargeEarlier;
BatchStore.prototype.isDischargeAdmixBeforeHeadWaterCompleted = dischargeMixin.isDischargeAdmixBeforeHeadWaterCompleted;
BatchStore.prototype.isDischargeAdmixBeforeWashWaterCompleted = dischargeMixin.isDischargeAdmixBeforeWashWaterCompleted;
BatchStore.prototype.isAllAdmixDischarged = dischargeMixin.isAllAdmixDischarged;
BatchStore.prototype.getActionTypeForScale = dischargeMixin.getActionTypeForScale;
BatchStore.prototype.getBatchStatusTypeForScale = dischargeMixin.getBatchStatusTypeForScale;
BatchStore.prototype.getConflictedAdmixWithScale = dischargeMixin.getConflictedAdmixWithScale;
BatchStore.prototype.getConflictedAdmixWithWater = dischargeMixin.getConflictedAdmixWithWater;
BatchStore.prototype.onWaterScaleHold = dischargeMixin.onWaterScaleHold;

// batch-emit-mixin
BatchStore.prototype.getBatchDataToEmit = batchEmitMixin.getBatchDataToEmit;
BatchStore.prototype.getOptimizedBatchData = batchEmitMixin.getOptimizedBatchData;
BatchStore.prototype.getGUIs = batchEmitMixin.getGUIs;
BatchStore.prototype.overrideBatchData = batchEmitMixin.overrideBatchData;
BatchStore.prototype.emittedDataHasNoChanges = batchEmitMixin.emittedDataHasNoChanges;
BatchStore.prototype.emittedDataToCompare = batchEmitMixin.emittedDataToCompare;
BatchStore.prototype.canControlPlant = batchEmitMixin.canControlPlant;
BatchStore.prototype.emitBatchData = batchEmitMixin.emitBatchData;
BatchStore.prototype.onWSClose = batchEmitMixin.onWSClose;
BatchStore.prototype.observeBatchData = batchEmitMixin.observeBatchData;

// batch-lifecycle-mixin
BatchStore.prototype.onStartReplayLoad = batchLifecycleMixin.onStartReplayLoad;
BatchStore.prototype.finishReplayLoad = batchLifecycleMixin.finishReplayLoad;
BatchStore.prototype.saveLog = batchLifecycleMixin.saveLog;
BatchStore.prototype.displayLogs = batchLifecycleMixin.displayLogs;
BatchStore.prototype.displayLogsNotification = batchLifecycleMixin.displayLogsNotification;
BatchStore.prototype.autoBatch = batchLifecycleMixin.autoBatch;
BatchStore.prototype.addToQueue = batchLifecycleMixin.addToQueue;
BatchStore.prototype.adjustAdmixturesForDrop = batchLifecycleMixin.adjustAdmixturesForDrop;
BatchStore.prototype.splitDataForDrops = batchLifecycleMixin.splitDataForDrops;
BatchStore.prototype.getAllWatersFromMixDesign = batchLifecycleMixin.getAllWatersFromMixDesign;
BatchStore.prototype.getHeadWashWaterTarget = batchLifecycleMixin.getHeadWashWaterTarget;
BatchStore.prototype.getTotalHeadWashWater = batchLifecycleMixin.getTotalHeadWashWater;
BatchStore.prototype.calculateDrops = batchLifecycleMixin.calculateDrops;
BatchStore.prototype.getInitialMaterialData = batchLifecycleMixin.getInitialMaterialData;
BatchStore.prototype.isDeviceDeferred = batchLifecycleMixin.isDeviceDeferred;
BatchStore.prototype.isSmallLoadSize = batchLifecycleMixin.isSmallLoadSize;
BatchStore.prototype.getFreeFall = batchLifecycleMixin.getFreeFall;
BatchStore.prototype.getInitialJogOpenTime = batchLifecycleMixin.getInitialJogOpenTime;
BatchStore.prototype.getJogCloseTime = batchLifecycleMixin.getJogCloseTime;

// scale-weight-mixin
BatchStore.prototype.setDecumScaleStatus = scaleWeightMixin.setDecumScaleStatus;
BatchStore.prototype.getCurrentWeightForBin = scaleWeightMixin.getCurrentWeightForBin;
BatchStore.prototype.getTargetForWater = scaleWeightMixin.getTargetForWater;
BatchStore.prototype.getTargetForWeighed = scaleWeightMixin.getTargetForWeighed;
BatchStore.prototype.getAnalogInputValue = scaleWeightMixin.getAnalogInputValue;
BatchStore.prototype.getActualSlumpData = scaleWeightMixin.getActualSlumpData;
BatchStore.prototype.checkIfMixIsDifferFromMixInTruck = scaleWeightMixin.checkIfMixIsDifferFromMixInTruck;
BatchStore.prototype.adjustMixDesignToMixInTruck = scaleWeightMixin.adjustMixDesignToMixInTruck;
BatchStore.prototype.setAutoStartTare = scaleWeightMixin.setAutoStartTare;
BatchStore.prototype.isScaleReadyForBatch = scaleWeightMixin.isScaleReadyForBatch;
BatchStore.prototype.getRemainingTargetForLastDrop = scaleWeightMixin.getRemainingTargetForLastDrop;
BatchStore.prototype.getNetTargetForScale = scaleWeightMixin.getNetTargetForScale;
BatchStore.prototype.getBinWeightForStatus = scaleWeightMixin.getBinWeightForStatus;
BatchStore.prototype.isActiveDrop = scaleWeightMixin.isActiveDrop;

// batch-material-mixin
BatchStore.prototype.batchMaterial = batchMaterialMixin.batchMaterial;
BatchStore.prototype.saveTargetForSkipMaterial = batchMaterialMixin.saveTargetForSkipMaterial;
BatchStore.prototype.saveTargetsForSkipMaterialsWithoutDevice = batchMaterialMixin.saveTargetsForSkipMaterialsWithoutDevice;
BatchStore.prototype.checkFinalWeightForManualBatch = batchMaterialMixin.checkFinalWeightForManualBatch;
BatchStore.prototype.monitorScaleInventory = batchMaterialMixin.monitorScaleInventory;
BatchStore.prototype.updateMaterialInventoryForPumps = batchMaterialMixin.updateMaterialInventoryForPumps;
BatchStore.prototype.setPumpWeight = batchMaterialMixin.setPumpWeight;
BatchStore.prototype.monitorPumpInventory = batchMaterialMixin.monitorPumpInventory;
BatchStore.prototype.getOverTargetFromPrevDrops = batchMaterialMixin.getOverTargetFromPrevDrops;

// manual-feed-mixin
BatchStore.prototype.onPumpButtonPress = manualFeedMixin.onPumpButtonPress;
BatchStore.prototype.onPumpButtonRelease = manualFeedMixin.onPumpButtonRelease;
BatchStore.prototype.isOtherBinStillFeeding = manualFeedMixin.isOtherBinStillFeeding;
BatchStore.prototype.onBinButtonDown = manualFeedMixin.onBinButtonDown;
BatchStore.prototype.onBinButtonUp = manualFeedMixin.onBinButtonUp;
BatchStore.prototype.isBinButtonPressed = manualFeedMixin.isBinButtonPressed;
BatchStore.prototype.onBinButtonPress = manualFeedMixin.onBinButtonPress;
BatchStore.prototype.setFeedInterruption = manualFeedMixin.setFeedInterruption;
BatchStore.prototype.onBinButtonRelease = manualFeedMixin.onBinButtonRelease;
BatchStore.prototype.isHoldFeedDevice = manualFeedMixin.isHoldFeedDevice;
BatchStore.prototype.onPausePlayFeedButtonPress = manualFeedMixin.onPausePlayFeedButtonPress;
BatchStore.prototype.holdFeedDevice = manualFeedMixin.holdFeedDevice;
BatchStore.prototype.unHoldFeedDevice = manualFeedMixin.unHoldFeedDevice;
BatchStore.prototype.stopAttachedDevices = manualFeedMixin.stopAttachedDevices;
BatchStore.prototype.shouldOpenButtonBeDisabled = manualFeedMixin.shouldOpenButtonBeDisabled;
BatchStore.prototype.isFinishFeedDevice = manualFeedMixin.isFinishFeedDevice;
BatchStore.prototype.isFinishDischargeDevice = manualFeedMixin.isFinishDischargeDevice;
BatchStore.prototype.onFinishFeedButtonPress = manualFeedMixin.onFinishFeedButtonPress;
BatchStore.prototype.onFinishDischargeButtonPress = manualFeedMixin.onFinishDischargeButtonPress;
BatchStore.prototype.onPumpResetButtonClick = manualFeedMixin.onPumpResetButtonClick;

// feed-mode-mixin
BatchStore.prototype.feedMode = feedModeMixin.feedMode;
BatchStore.prototype.checkFeedMetrics = feedModeMixin.checkFeedMetrics;
BatchStore.prototype.adjustWaterTarget = feedModeMixin.adjustWaterTarget;
BatchStore.prototype.setFeedFinish = feedModeMixin.setFeedFinish;

// jog-mode-mixin
BatchStore.prototype.getScaleWeightAfterSettlingTime = jogModeMixin.getScaleWeightAfterSettlingTime;
BatchStore.prototype.updateInventory = jogModeMixin.updateInventory;
BatchStore.prototype.updateInventoryOnPause = jogModeMixin.updateInventoryOnPause;
BatchStore.prototype.jogMode = jogModeMixin.jogMode;
BatchStore.prototype.finishFeed = jogModeMixin.finishFeed;
BatchStore.prototype.checkWeightStatus = jogModeMixin.checkWeightStatus;
BatchStore.prototype.checkDecumScaleWeight = jogModeMixin.checkDecumScaleWeight;
BatchStore.prototype.checkAddingWeightToDecumScale = jogModeMixin.checkAddingWeightToDecumScale;

// jog-helpers-mixin
BatchStore.prototype.checkScaleWeightInTestMode = jogHelpersMixin.checkScaleWeightInTestMode;
BatchStore.prototype.checkPumpInTestMode = jogHelpersMixin.checkPumpInTestMode;
BatchStore.prototype.checkScaleWeightForMaterial = jogHelpersMixin.checkScaleWeightForMaterial;
BatchStore.prototype.checkFeedWeight = jogHelpersMixin.checkFeedWeight;
BatchStore.prototype.getTolerance = jogHelpersMixin.getTolerance;
BatchStore.prototype.getToleranceByPercent = jogHelpersMixin.getToleranceByPercent;
BatchStore.prototype.getToleranceOver = jogHelpersMixin.getToleranceOver;
BatchStore.prototype.getToleranceUnder = jogHelpersMixin.getToleranceUnder;
BatchStore.prototype.getJogOpenTime = jogHelpersMixin.getJogOpenTime;
BatchStore.prototype.openJog = jogHelpersMixin.openJog;
BatchStore.prototype.closeJog = jogHelpersMixin.closeJog;
BatchStore.prototype.runJogTest = jogHelpersMixin.runJogTest;
BatchStore.prototype.stopJogTest = jogHelpersMixin.stopJogTest;

// pump-metered-mixin
BatchStore.prototype.getPumpWeight = pumpMeteredMixin.getPumpWeight;
BatchStore.prototype.getTotalWeightInActiveLoad = pumpMeteredMixin.getTotalWeightInActiveLoad;
BatchStore.prototype.getTargetForMetered = pumpMeteredMixin.getTargetForMetered;
BatchStore.prototype.hasPumpFinished = pumpMeteredMixin.hasPumpFinished;
BatchStore.prototype.isInputPortOff = pumpMeteredMixin.isInputPortOff;
BatchStore.prototype.isPumpNotEmpty = pumpMeteredMixin.isPumpNotEmpty;
BatchStore.prototype.isPumpEmpty = pumpMeteredMixin.isPumpEmpty;
BatchStore.prototype.isTankClosed = pumpMeteredMixin.isTankClosed;
BatchStore.prototype.isTankEmpty = pumpMeteredMixin.isTankEmpty;
BatchStore.prototype.getUnitById = pumpMeteredMixin.getUnitById;
BatchStore.prototype.isBottlePump = pumpMeteredMixin.isBottlePump;
BatchStore.prototype.isDirectFeedPump = pumpMeteredMixin.isDirectFeedPump;
BatchStore.prototype.isPumpWithTank = pumpMeteredMixin.isPumpWithTank;
BatchStore.prototype.restartHeadWaterPump = pumpMeteredMixin.restartHeadWaterPump;
BatchStore.prototype.startHeadWater = pumpMeteredMixin.startHeadWater;
BatchStore.prototype.startWashWaterButtonShouldBeVisible = pumpMeteredMixin.startWashWaterButtonShouldBeVisible;
BatchStore.prototype.startWashWater = pumpMeteredMixin.startWashWater;
BatchStore.prototype.batchMetered = pumpMeteredMixin.batchMetered;
BatchStore.prototype.startExtraWater = pumpMeteredMixin.startExtraWater;

// pump-discharge-mixin
BatchStore.prototype.onPumpFinishFeed = pumpDischargeMixin.onPumpFinishFeed;
BatchStore.prototype.checkSetMeterCommandSent = pumpDischargeMixin.checkSetMeterCommandSent;
BatchStore.prototype.checkPumpFinishDischarge = pumpDischargeMixin.checkPumpFinishDischarge;
BatchStore.prototype.dischargeBottlePump = pumpDischargeMixin.dischargeBottlePump;
BatchStore.prototype.checkPumpIsEmpty = pumpDischargeMixin.checkPumpIsEmpty;
BatchStore.prototype.resumePrimaryScaleDischarge = pumpDischargeMixin.resumePrimaryScaleDischarge;
BatchStore.prototype.isAdmixturePriorityReady = pumpDischargeMixin.isAdmixturePriorityReady;
BatchStore.prototype.onFinishNonBottlePump = pumpDischargeMixin.onFinishNonBottlePump;
BatchStore.prototype.checkTankIsEmpty = pumpDischargeMixin.checkTankIsEmpty;
BatchStore.prototype.checkTankIsClosed = pumpDischargeMixin.checkTankIsClosed;
BatchStore.prototype.dischargeTank = pumpDischargeMixin.dischargeTank;
BatchStore.prototype.checkScalesIfWaitingForWater = pumpDischargeMixin.checkScalesIfWaitingForWater;
BatchStore.prototype.checkPumpFinishFeed = pumpDischargeMixin.checkPumpFinishFeed;
BatchStore.prototype.checkCanDischargePump = pumpDischargeMixin.checkCanDischargePump;
BatchStore.prototype.checkMeteredWeight = pumpDischargeMixin.checkMeteredWeight;

// batch-action-mixin
BatchStore.prototype.onBatchAction = batchActionMixin.onBatchAction;
BatchStore.prototype.getMeteredDataToBatch = batchActionMixin.getMeteredDataToBatch;
BatchStore.prototype.getWeighedDataToBatch = batchActionMixin.getWeighedDataToBatch;
BatchStore.prototype.getFinalScale = batchActionMixin.getFinalScale;
BatchStore.prototype.getDataForPumpsAndScales = batchActionMixin.getDataForPumpsAndScales;
BatchStore.prototype.sendBatchCycleFeedStartEvent = batchActionMixin.sendBatchCycleFeedStartEvent;
BatchStore.prototype.checkDataInScaleQueue = batchActionMixin.checkDataInScaleQueue;
BatchStore.prototype.isOneOfScalesReady = batchActionMixin.isOneOfScalesReady;
BatchStore.prototype.arePumpsReadyForBatch = batchActionMixin.arePumpsReadyForBatch;
BatchStore.prototype.holdScalesClosed = batchActionMixin.holdScalesClosed;
BatchStore.prototype.setLoadStartTime = batchActionMixin.setLoadStartTime;
BatchStore.prototype.canBatchMetered = batchActionMixin.canBatchMetered;
BatchStore.prototype.canStartBatchingNextTicketInQueue = batchActionMixin.canStartBatchingNextTicketInQueue;
BatchStore.prototype.startBatch = batchActionMixin.startBatch;

// start-batch-mixin
BatchStore.prototype.checkIfFirstMaterialForScaleIsTurboBatch = startBatchMixin.checkIfFirstMaterialForScaleIsTurboBatch;
BatchStore.prototype.showTargetsOnPumps = startBatchMixin.showTargetsOnPumps;
BatchStore.prototype.setActiveBinByBay = startBatchMixin.setActiveBinByBay;
BatchStore.prototype.showTargetOnBins = startBatchMixin.showTargetOnBins;
BatchStore.prototype.batchDataForScale = startBatchMixin.batchDataForScale;
BatchStore.prototype.isScaleReadyForDischargeToNextScale = startBatchMixin.isScaleReadyForDischargeToNextScale;
BatchStore.prototype.isPrevLoadHasFinishDischargeToNextScale = startBatchMixin.isPrevLoadHasFinishDischargeToNextScale;
BatchStore.prototype.allUpperScalesAreReadyForDischarge = startBatchMixin.allUpperScalesAreReadyForDischarge;
BatchStore.prototype.isNextScaleReadyForFeed = startBatchMixin.isNextScaleReadyForFeed;
BatchStore.prototype.goToDischarge = startBatchMixin.goToDischarge;
BatchStore.prototype.getScalePorts = startBatchMixin.getScalePorts;
BatchStore.prototype.setTargetsForDecumScale = startBatchMixin.setTargetsForDecumScale;
BatchStore.prototype.dischargeDecumScale = startBatchMixin.dischargeDecumScale;

// pac-scale-mixin
BatchStore.prototype.jogModeDecum = pacScaleMixin.jogModeDecum;
BatchStore.prototype.openJogDecum = pacScaleMixin.openJogDecum;
BatchStore.prototype.closeJogDecum = pacScaleMixin.closeJogDecum;
BatchStore.prototype.isIndicatorValueError = pacScaleMixin.isIndicatorValueError;
BatchStore.prototype.checkIndicatorValueError = pacScaleMixin.checkIndicatorValueError;
BatchStore.prototype.checkIndicatorError = pacScaleMixin.checkIndicatorError;
BatchStore.prototype.checkBeltsInterlock = pacScaleMixin.checkBeltsInterlock;
BatchStore.prototype.checkManualPanel = pacScaleMixin.checkManualPanel;
BatchStore.prototype.setManualLoad = pacScaleMixin.setManualLoad;
BatchStore.prototype.checkPacConnected = pacScaleMixin.checkPacConnected;
BatchStore.prototype.checkPacConnection = pacScaleMixin.checkPacConnection;
BatchStore.prototype.getStatus = pacScaleMixin.getStatus;
BatchStore.prototype.onProxyDisconnect = pacScaleMixin.onProxyDisconnect;
BatchStore.prototype.convertInputsAndOutputs = pacScaleMixin.convertInputsAndOutputs;
BatchStore.prototype.onProxyDataTestMode = pacScaleMixin.onProxyDataTestMode;
BatchStore.prototype.onProxyData = pacScaleMixin.onProxyData;
BatchStore.prototype.start = pacScaleMixin.start;
BatchStore.prototype.stop = pacScaleMixin.stop;
BatchStore.prototype.getScaleWeight = pacScaleMixin.getScaleWeight;
BatchStore.prototype.isInputDisabled = pacScaleMixin.isInputDisabled;
BatchStore.prototype.isScaleOpen = pacScaleMixin.isScaleOpen;
BatchStore.prototype.isDoubleGateScale = pacScaleMixin.isDoubleGateScale;
BatchStore.prototype.onScaleClose = pacScaleMixin.onScaleClose;
BatchStore.prototype.openScaleWithMode = pacScaleMixin.openScaleWithMode;
BatchStore.prototype.openScale = pacScaleMixin.openScale;
BatchStore.prototype.closeScale = pacScaleMixin.closeScale;
BatchStore.prototype.isDecumScaleOpen = pacScaleMixin.isDecumScaleOpen;
BatchStore.prototype.closeDecumScale = pacScaleMixin.closeDecumScale;
BatchStore.prototype.onDeviceStatus = pacScaleMixin.onDeviceStatus;

// discharge-sequence-mixin
BatchStore.prototype.isTheSameDataInNextScale = dischargeSequenceMixin.isTheSameDataInNextScale;
BatchStore.prototype.dischargeToNextScale = dischargeSequenceMixin.dischargeToNextScale;
BatchStore.prototype.showReadyForDischarge = dischargeSequenceMixin.showReadyForDischarge;
BatchStore.prototype.shouldWaitOnOtherScaleDischarge = dischargeSequenceMixin.shouldWaitOnOtherScaleDischarge;
BatchStore.prototype.checkDischargeToScale = dischargeSequenceMixin.checkDischargeToScale;
BatchStore.prototype.checkDecumDischargeToNextScale = dischargeSequenceMixin.checkDecumDischargeToNextScale;
BatchStore.prototype.isReadyForFinalDischarge = dischargeSequenceMixin.isReadyForFinalDischarge;
BatchStore.prototype.getSequence = dischargeSequenceMixin.getSequence;
BatchStore.prototype.toggleMixer = dischargeSequenceMixin.toggleMixer;
BatchStore.prototype.isBootDeviceReadyForDischarge = dischargeSequenceMixin.isBootDeviceReadyForDischarge;
BatchStore.prototype.isMixerOn = dischargeSequenceMixin.isMixerOn;
BatchStore.prototype.mixerIsReadyForDischarge = dischargeSequenceMixin.mixerIsReadyForDischarge;
BatchStore.prototype.getTemperWater = dischargeSequenceMixin.getTemperWater;
BatchStore.prototype.getTemperWaterData = dischargeSequenceMixin.getTemperWaterData;
BatchStore.prototype.checkBatchProgress = dischargeSequenceMixin.checkBatchProgress;
BatchStore.prototype.printAsSoonAsMaterialsAreWeighed = dischargeSequenceMixin.printAsSoonAsMaterialsAreWeighed;
BatchStore.prototype.dischargeMixer = dischargeSequenceMixin.dischargeMixer;
BatchStore.prototype.checkMixerFinishReturning = dischargeSequenceMixin.checkMixerFinishReturning;
BatchStore.prototype.checkBeltsRunning = dischargeSequenceMixin.checkBeltsRunning;
BatchStore.prototype.isMixerDischargeAllowed = dischargeSequenceMixin.isMixerDischargeAllowed;
BatchStore.prototype.isMixerFinishAllowed = dischargeSequenceMixin.isMixerFinishAllowed;
BatchStore.prototype.rampUpMixer = dischargeSequenceMixin.rampUpMixer;

// discharge-primary-mixin
BatchStore.prototype.finishDischarge = dischargePrimaryMixin.finishDischarge;
BatchStore.prototype.pauseDischargeScale = dischargePrimaryMixin.pauseDischargeScale;
BatchStore.prototype.resumeDischargeScale = dischargePrimaryMixin.resumeDischargeScale;
BatchStore.prototype.isMoreDrops = dischargePrimaryMixin.isMoreDrops;
BatchStore.prototype.onDischargeFinish = dischargePrimaryMixin.onDischargeFinish;
BatchStore.prototype.convertWater = dischargePrimaryMixin.convertWater;
BatchStore.prototype.updateScaleDischarge = dischargePrimaryMixin.updateScaleDischarge;

// discharge-helpers-mixin
BatchStore.prototype.dischargeWithSequence = dischargeHelpersMixin.dischargeWithSequence;
BatchStore.prototype.areAllPumpsFinished = dischargeHelpersMixin.areAllPumpsFinished;
BatchStore.prototype.checkPumpsFinish = dischargeHelpersMixin.checkPumpsFinish;
BatchStore.prototype.getPrimaryCumulative = dischargeHelpersMixin.getPrimaryCumulative;
BatchStore.prototype.checkIfWashWaterReadyFromSecondary = dischargeHelpersMixin.checkIfWashWaterReadyFromSecondary;
BatchStore.prototype.onPrimaryDischarge = dischargeHelpersMixin.onPrimaryDischarge;
BatchStore.prototype.onSecondaryScaleDischarge = dischargeHelpersMixin.onSecondaryScaleDischarge;
BatchStore.prototype.dischargeWaterIfReady = dischargeHelpersMixin.dischargeWaterIfReady;
BatchStore.prototype.onWaterFirstDischargeChange = dischargeHelpersMixin.onWaterFirstDischargeChange;
BatchStore.prototype.updateSequenceTargets = dischargeHelpersMixin.updateSequenceTargets;
BatchStore.prototype.checkDischargeAuthorizationInput = dischargeHelpersMixin.checkDischargeAuthorizationInput;
BatchStore.prototype.isMultiBatchMixerReadyForAnotherDrop = dischargeHelpersMixin.isMultiBatchMixerReadyForAnotherDrop;
BatchStore.prototype.discharge = dischargeHelpersMixin.discharge;
BatchStore.prototype.getDecumTarget = dischargeHelpersMixin.getDecumTarget;
BatchStore.prototype.getDecumTolerance = dischargeHelpersMixin.getDecumTolerance;
BatchStore.prototype.checkScalesDischargeFinish = dischargeHelpersMixin.checkScalesDischargeFinish;
BatchStore.prototype.isScaleSwitchGatePortOn = dischargeHelpersMixin.isScaleSwitchGatePortOn;

// control-mixin
BatchStore.prototype.onFlowRateDown = controlMixin.onFlowRateDown;
BatchStore.prototype.onFlowRateUp = controlMixin.onFlowRateUp;
BatchStore.prototype.flowRateUp = controlMixin.flowRateUp;
BatchStore.prototype.flowRateDown = controlMixin.flowRateDown;
BatchStore.prototype.onFeedFlowRateChange = controlMixin.onFeedFlowRateChange;
BatchStore.prototype.override = controlMixin.override;
BatchStore.prototype.overrideDevice = controlMixin.overrideDevice;
BatchStore.prototype.demo = controlMixin.demo;
BatchStore.prototype.checkScalesAndClose = controlMixin.checkScalesAndClose;
BatchStore.prototype.closeBin = controlMixin.closeBin;
BatchStore.prototype.holdBin = controlMixin.holdBin;
BatchStore.prototype.removeLoad = controlMixin.removeLoad;
BatchStore.prototype.holdFeed = controlMixin.holdFeed;
BatchStore.prototype.resumeFeed = controlMixin.resumeFeed;
BatchStore.prototype.isHoldScale = controlMixin.isHoldScale;
BatchStore.prototype.resumeScaleId = controlMixin.resumeScaleId;
BatchStore.prototype.holdScaleId = controlMixin.holdScaleId;
BatchStore.prototype.holdScale = controlMixin.holdScale;
BatchStore.prototype.getInitialOpenTimeForScale = controlMixin.getInitialOpenTimeForScale;
BatchStore.prototype.resumeScale = controlMixin.resumeScale;
BatchStore.prototype.finishDischargeScaleEarlier = controlMixin.finishDischargeScaleEarlier;
BatchStore.prototype.holdDischarge = controlMixin.holdDischarge;
BatchStore.prototype.resumeDischarge = controlMixin.resumeDischarge;
BatchStore.prototype.sendAbortCommands = controlMixin.sendAbortCommands;
BatchStore.prototype.abort = controlMixin.abort;
BatchStore.prototype.onContaminationChange = controlMixin.onContaminationChange;
BatchStore.prototype.checkContamination = controlMixin.checkContamination;
BatchStore.prototype.isContaminationDetected = controlMixin.isContaminationDetected;
BatchStore.prototype.showContaminationPopup = controlMixin.showContaminationPopup;
BatchStore.prototype.getContaminationDataFromOutput = controlMixin.getContaminationDataFromOutput;
BatchStore.prototype.getContaminationOutputsToCheck = controlMixin.getContaminationOutputsToCheck;
BatchStore.prototype.resumeStoppedDevices = controlMixin.resumeStoppedDevices;
BatchStore.prototype.emergencyStop = controlMixin.emergencyStop;
BatchStore.prototype.onOverToleranceChange = controlMixin.onOverToleranceChange;
BatchStore.prototype.showOverTolerancePopup = controlMixin.showOverTolerancePopup;
BatchStore.prototype.isModalVisible = controlMixin.isModalVisible;

// init-listeners-mixin
BatchStore.prototype.checkProxyNeedsUpdate = initListenersMixin.checkProxyNeedsUpdate;
BatchStore.prototype.initListenerAfterMaterialsLoaded = initListenersMixin.initListenerAfterMaterialsLoaded;
BatchStore.prototype.initListeners = initListenersMixin.initListeners;
BatchStore.prototype.isPacUidValid = initListenersMixin.isPacUidValid;
BatchStore.prototype.getPacVersion = initListenersMixin.getPacVersion;
BatchStore.prototype.checkMixerNoseplug = initListenersMixin.checkMixerNoseplug;
BatchStore.prototype.mixerNoseplugIsActive = initListenersMixin.mixerNoseplugIsActive;
BatchStore.prototype.switchButtonShouldBeDisabled = initListenersMixin.switchButtonShouldBeDisabled;

// batch-init-mixin
BatchStore.prototype.initData = batchInitMixin.initData;
BatchStore.prototype.changeMaterialMidBatch = batchInitMixin.changeMaterialMidBatch;

// batch-helpers-mixin
BatchStore.prototype.setTimeout = batchHelpersMixin.setTimeout;
BatchStore.prototype.clearTimeout = batchHelpersMixin.clearTimeout;
BatchStore.prototype.isOutputOn = batchHelpersMixin.isOutputOn;
BatchStore.prototype.compareData = batchHelpersMixin.compareData;
BatchStore.prototype.getNumberOfLoadsAndQueue = batchHelpersMixin.getNumberOfLoadsAndQueue;
BatchStore.prototype.getNumberOfDeferredDevicesForScale = batchHelpersMixin.getNumberOfDeferredDevicesForScale;
BatchStore.prototype.setOrderId = batchHelpersMixin.setOrderId;
BatchStore.prototype.setMaterialSubstitutionData = batchHelpersMixin.setMaterialSubstitutionData;

// bin-scale-data-mixin
BatchStore.prototype.getBins = binScaleDataMixin.getBins;
BatchStore.prototype.isBeltOn = binScaleDataMixin.isBeltOn;
BatchStore.prototype.getFirstBinIdForTheScale = binScaleDataMixin.getFirstBinIdForTheScale;
BatchStore.prototype.getFirstMaterialForTheScale = binScaleDataMixin.getFirstMaterialForTheScale;
BatchStore.prototype.getLastMaterialForTheScale = binScaleDataMixin.getLastMaterialForTheScale;

// @ts-ignore
interface BatchStore extends IBatchStore {}
/** BatchStore with all functional mixin methods applied — use this type instead of the raw class. */
export type BatchStoreWithMixins = BatchStore & IBatchStore;

export default BatchStore;
